import React, { useContext, useCallback } from 'react';
import { Paper, BottomNavigation, BottomNavigationAction, Box, Fab, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import HelpIcon from '@mui/icons-material/Help';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { AuthContext } from '../context/AuthContext';
import OptimizedImage from './OptimizedImage';
import NotificationMenu from './NotificationMenu';
import newMatchIcon from '../assets/new-match-icon.png';

function BottomNav({ newMatchButtonSize = 1 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isNewMatchPage = location.pathname === '/new-match';

  const getCurrentValue = useCallback(() => {
    const path = location.pathname;
    if (path === '/') return 0;
    if (path.startsWith('/torneos')) return 1;
    if (path === '/my-matches') return 3;
    if (path === '/profile') return 4;
    return -1;
  }, [location.pathname]);

  // Early return if no user or on login/register pages
  if (!user || location.pathname === '/login' || location.pathname === '/register') {
    return null;
  }

  return (
    <>
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          height: 52.5,
          zIndex: 1000,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider'
        }} 
        elevation={3}
      >
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: isNewMatchPage ? -10 : -28.5,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Fab
              color="primary"
              sx={{
                width: isNewMatchPage ? 45 : 58.5 * newMatchButtonSize,
                height: isNewMatchPage ? 45 : 58.5 * newMatchButtonSize,
                backgroundColor: 'primary.main',
                padding: 0,
                overflow: 'hidden',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                  transform: 'scale(1.1)',
                },
              }}
              onClick={() => navigate('/new-match')}
            >
              <OptimizedImage
                src={newMatchIcon}
                alt="Nueva Partida"
                width={isNewMatchPage ? 60 : 81 * newMatchButtonSize}
                height={isNewMatchPage ? 60 : 81 * newMatchButtonSize}
              />
            </Fab>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.675rem',
                color: 'text.secondary',
                marginTop: '2px',
              }}
            >
              Nueva Partida
            </Typography>
          </Box>

          <BottomNavigation
            value={getCurrentValue()}
            onChange={(event, newValue) => {
              if (newValue === 4) {
                event.preventDefault();
                return;
              }

              switch (newValue) {
                case 0:
                  navigate('/');
                  break;
                case 1:
                  navigate('/torneos');
                  break;
                case 3:
                  navigate('/my-matches');
                  break;
              }
            }}
            sx={{
              height: '100%',
              bgcolor: 'background.paper',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                padding: '5px 11px',
                color: 'text.secondary',
                '& .MuiSvgIcon-root': {
                  fontSize: 25,
                },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.79rem',
                  opacity: 1,
                  '&.Mui-selected': {
                    fontSize: '0.79rem'
                  }
                }
              },
              '& .Mui-selected': {
                color: 'primary.main',
              },
            }}
          >
            <BottomNavigationAction 
              label="Inicio" 
              icon={<HomeIcon />} 
            />
            <BottomNavigationAction 
              label="Torneos" 
              icon={<EmojiEventsIcon />} 
            />
            <BottomNavigationAction 
              sx={{ visibility: 'hidden' }}
            />
            <BottomNavigationAction 
              label="Partidas" 
              icon={<SportsEsportsIcon />}
            />
            <BottomNavigationAction
              icon={<NotificationMenu />}
              sx={{
                '& .MuiBottomNavigationAction-label': {
                  display: 'none'
                },
                '& .MuiBottomNavigationAction-root': {
                  minWidth: 'auto',
                  padding: '5px 11px',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'primary.main'
                  }
                }
              }}
            />
          </BottomNavigation>
        </Box>
      </Paper>
    </>
  );
}

export default React.memo(BottomNav); 